import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { CircleAlertIcon, LockIcon, UploadIcon } from 'lucide-react'

import { Progress } from '@ark-ui/react/progress'
import { tv } from 'tailwind-variants'

import {
  isDraggingFiles,
  isDragLeavingBounds,
  preventDefaults,
} from '../../../../utils/events'
import { useGridSnap } from '../../grid/grid.store'
import { useCellKey } from '../cell.context'
import { useCellIsReadOnly } from '../cell.hooks'
import { StatusFrame } from '../CellFrame'

import {
  CellUploadWidgetCompleted,
  CellUploadWidgetFailed,
  CellUploadWidgetUploading,
} from './CellUploadWidget'
import { cellUploadActions, getCellUploadProgress } from './upload'

/**
 * Handles drag and drop and file upload for a cell.
 *
 * @note Only for cells in `richtext` columns. `select` type doesn't support uploads.
 *
 * Wraps cell content in a dropzone. If any cell upload state exists,
 * the dropzone is not rendered to prevent overriding existing uploads.
 */
export function CellUpload({ children }: React.ComponentProps<'div'>) {
  const cellKey = useCellKey()

  const snap = useGridSnap()
  const uploadState = snap.cellUploadRegistry[cellKey]

  if (uploadState?.status) {
    return <CellUploadOverlay />
  } else {
    return <CellDropzone>{children}</CellDropzone>
  }
}

/**
 * Dropzone component that wraps Cell content and shows an overlay
 * indicating whether cell accepts upload or is readOnly.
 */
function CellDropzone({ children }: React.ComponentProps<'div'>) {
  const cellKey = useCellKey()

  const isReadOnly = useCellIsReadOnly()

  const [isDraggedOver, setIsDraggedOver] = useState<boolean>(false)

  const handleDragEnter = (e: React.DragEvent) => {
    if (!isDraggingFiles(e)) return false

    setIsDraggedOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    if (!isDraggingFiles(e)) return false
    if (!isDragLeavingBounds(e)) return false

    setIsDraggedOver(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    if (!isDraggingFiles(e)) return false

    // Override document handler to register
    e.preventDefault()
    e.stopPropagation()

    // Set drop cursor conditionally
    e.dataTransfer.dropEffect = isReadOnly ? 'none' : 'copy'
  }

  const handleDrop = (e: React.DragEvent) => {
    // Register for drop (only runs if dragOver is registered)
    e.preventDefault()
    e.stopPropagation()

    // Convert array-like `Filelist` to actual `File[]`
    const files = Array.from(e.dataTransfer.files)

    // Process uploads async
    cellUploadActions
      .startCellUpload(cellKey, files)
      .then(() => {})
      .catch(() => {})

    // Reset state
    setIsDraggedOver(false)
  }

  return (
    <div
      role="none" // a11y: Dropzone only activated by drag, so we remove it from tree
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className="w-full h-full"
    >
      <DropOverlay
        isDraggedOver={isDraggedOver}
        isReadOnly={isReadOnly}
      >
        <DropOverlayContent />
      </DropOverlay>

      {/* Cell content */}
      {children}
    </div>
  )
}

const sharedOverlayStyle = tv({
  base: 'bg-black/75 backdrop-blur-sm rounded-sm',
})

const sharedOverlayContentStyle = tv({
  base: [
    'bg-black rounded-md',
    'min-w-8 min-h-8', // For consistent icon placement
    'flex flex-col items-center justify-center gap-1 px-2 py-1',
  ],
})

const dropOverlayStyle = tv({
  extend: sharedOverlayStyle,
  base: [
    'absolute inset-0 z-10',
    'flex items-center justify-center',
    'outline-2 outline-dashed outline-transparent',
    'ring-2 ring-black', // Fix: prevent parent outlines from showing through dashed gaps
  ],
  variants: {
    isDraggedOver: {
      true: 'outline-blue-500',
    },
    isReadOnly: {
      true: 'outline-transparent',
    },
  },
})

function DropOverlay({
  isDraggedOver,
  isReadOnly,
  children,
}: {
  isDraggedOver: boolean
  isReadOnly: boolean
  children: React.ReactNode
}) {
  if (!isDraggedOver) return null

  return (
    <div
      // Prevent selecting or activating content
      onClick={preventDefaults}
      onDoubleClick={preventDefaults}
      onMouseDown={preventDefaults}
      className={dropOverlayStyle({
        isDraggedOver: isDraggedOver,
        isReadOnly: isReadOnly,
      })}
    >
      <span className={sharedOverlayContentStyle()}>{children}</span>
    </div>
  )
}

function DropOverlayContent() {
  const isReadOnly = useCellIsReadOnly()

  if (isReadOnly) {
    return <LockIcon className="size-6 text-yellow-400" />
  }

  return <UploadIcon className="size-6 text-blue-500" />
}

/**
 * Upload overlay is not absolutely positioned, because it needs to grow when
 * multiple items are uploaded, so that a small cell doesn't cut off the
 * overlay.
 */
const uploadOverlayStyle = tv({
  extend: sharedOverlayStyle,
  base: [
    'w-full h-full',
    'justify-center content-center', // Center horizontally
    'px-1.5', // Pad files horizontally
  ],
})

const uploadOverlayContentStyle = tv({
  extend: sharedOverlayContentStyle,
  base: [
    'max-w-fit mx-auto', // Center and constrain
  ],
})

/**
 * Cell upload overlay component that renders state of the batch upload
 * and state of each individual file.
 */
function CellUploadOverlay() {
  return (
    <div className={uploadOverlayStyle()}>
      <UploadState />
      <UploadStateFiles />
    </div>
  )
}

function UploadState() {
  const cellKey = useCellKey()

  const snap = useGridSnap()
  const uploadState = snap.cellUploadRegistry[cellKey]

  if (!uploadState) return null

  switch (uploadState.status) {
    case 'uploading':
      return (
        <div className={uploadOverlayContentStyle({ className: 'text-sm' })}>
          <StatusFrame variant="pending" />

          <UploadStateProgress />
          <button
            onClick={(e) => {
              preventDefaults(e)
              cellUploadActions.cancelCellUpload(cellKey)
            }}
            className="text-xs font-semibold text-gray-500 hover:text-gray-300 focus:outline-hidden"
          >
            Cancel
          </button>
        </div>
      )

    case 'success':
      return (
        <div className={uploadOverlayContentStyle()}>
          <UploadStateProgress />
          <span className="text-xs">Saving</span>
        </div>
      )

    case 'error':
      return (
        <div className={uploadOverlayContentStyle()}>
          <StatusFrame variant="error" />

          <CircleAlertIcon className="size-7.5 text-red-400" />

          <span className="text-xs my-1">{uploadState.error ?? 'Error'}</span>

          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              preventDefaults(e)
              cellUploadActions.clearCellUpload(cellKey)
            }}
          >
            Clear
          </Button>
        </div>
      )

    default: {
      const _exhaustive: never = uploadState
      return _exhaustive
    }
  }
}

function UploadStateProgress() {
  const cellKey = useCellKey()
  const snap = useGridSnap()
  const { isTransferring, totalProgress } = getCellUploadProgress(snap, cellKey)

  // Show indeterminate spinner (`null`) before upload starts
  const progressValue = isTransferring ? totalProgress : null

  return (
    <Progress.Root
      value={progressValue}
      min={0}
      max={100}
      className="flex flex-col items-center gap-2 py-1"
    >
      <div className="relative inline-flex items-center justify-center">
        <Progress.Circle className="[--size:2.5rem] [--thickness:0.25rem] data-[state=indeterminate]:animate-spin">
          <Progress.CircleTrack className="stroke-gray-700" />
          <Progress.CircleRange className="stroke-blue-500 data-[state=indeterminate]:[stroke-dasharray:40_100]" />
        </Progress.Circle>
        {isTransferring && (
          <Progress.ValueText className="absolute text-[0.65rem] font-semibold" />
        )}
      </div>
    </Progress.Root>
  )
}

function UploadStateFiles() {
  const cellKey = useCellKey()

  const snap = useGridSnap()
  const uploadState = snap.cellUploadRegistry[cellKey]

  if (!uploadState) return null

  return Object.entries(uploadState.filesMap).map(([id, fileState]) => {
    switch (fileState.status) {
      case 'uploading':
        return (
          <CellUploadWidgetUploading
            key={id}
            fileName={fileState.name}
            fileSize={fileState.size}
            progress={fileState.progress}
          />
        )

      case 'completed':
        return (
          <CellUploadWidgetCompleted
            key={id}
            fileName={fileState.name}
            fileSize={fileState.size}
          />
        )

      case 'failed':
        return (
          <CellUploadWidgetFailed
            key={id}
            fileName={fileState.name}
            error={fileState.error}
          />
        )

      default: {
        const _exhaustive: never = fileState
        return _exhaustive
      }
    }
  })
}
